import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MultiStep, MultiStepItemProps } from '../MultiStepContainer';

interface UseMultiStepNavigationProps<T extends MultiStepItemProps> {
  steps: MultiStep<T>[];
  defaultStep?: string;
  beforeStepChange?: (from: string, to: string) => boolean | Promise<boolean>;
  onStepChange?: (stepId: string) => void;
  isDirty?: boolean;
}

export const useMultiStepNavigation = <T extends MultiStepItemProps>({
  steps,
  defaultStep,
  beforeStepChange,
  onStepChange,
  isDirty,
}: UseMultiStepNavigationProps<T>) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse step and substep from URL
  const parseUrlPath = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const urlStepId = pathParts[1] || defaultStep || steps[0].id;
    const urlSubstepId = pathParts[2];
    return { urlStepId, urlSubstepId };
  };

  const { urlStepId, urlSubstepId } = parseUrlPath();
  const [nextLoading, setNextLoading] = useState(false);
  const [isDirtyAndNext, setIsDirtyAndNext] = useState(false);

  useEffect(() => {
    if (!isDirty && isDirtyAndNext) {
      setIsDirtyAndNext(false);

      setTimeout(() => {
        handleNext();
      }, 1000);
    }
  }, [isDirty, isDirtyAndNext]);

  // Find current step and indices
  const getCurrentStepInfo = () => {
    const stepIndex = steps.findIndex((s) => s.id === urlStepId);
    const currentStepIndex = stepIndex >= 0 ? stepIndex : 0;
    const currentStep = steps[currentStepIndex];

    // Find the next available step for navigation
    const getNextAvailableStep = () => {
      for (let i = currentStepIndex + 1; i < steps.length; i++) {
        if (steps[i].available !== false) { // available is true or undefined
          return steps[i];
        }
      }
      return null;
    };
    
    // Find the previous available step for navigation
    const getPreviousAvailableStep = () => {
      for (let i = currentStepIndex - 1; i >= 0; i--) {
        if (steps[i].available !== false) { // available is true or undefined
          return steps[i];
        }
      }
      return null;
    };
    
    const nextAvailableStep = getNextAvailableStep();
    const previousAvailableStep = getPreviousAvailableStep();
    const isLastStep = !nextAvailableStep;
    const isFirstStep = !previousAvailableStep;

    return {
      currentStep,
      currentStepIndex,
      isLastStep,
      isFirstStep,
      nextAvailableStep,
      previousAvailableStep,
    };
  };

  const { currentStep, currentStepIndex, isLastStep, isFirstStep, nextAvailableStep, previousAvailableStep } = getCurrentStepInfo();

  // Handle case where current step becomes unavailable
  useEffect(() => {
    if (currentStep.available === false && steps.length > 0) {
      // Current step became unavailable, redirect to next available step or first step
      const redirectStep = nextAvailableStep || steps.find(s => s.available !== false) || steps[0];
      if (redirectStep && redirectStep.id !== currentStep.id) {
        goToStep(redirectStep.id);
      }
    }
  }, [currentStep.available, steps, currentStep.id, nextAvailableStep]);

  // Substep state management
  const [currentSubstepId, setCurrentSubstepId] = useState<string | undefined>(urlSubstepId);
  const submitFns = useRef<( () => Promise<boolean> | boolean )[]>([]);
  
  useEffect(() => {
    setCurrentSubstepId(urlSubstepId);
  }, [urlSubstepId]);

  // Core navigation function
  const goToStep = async (stepId: string, substepId?: string) => {
    const from = currentStep.id + (currentSubstepId ? `:${currentSubstepId}` : '');
    const to = stepId + (substepId ? `:${substepId}` : '');

    // beforeStepChange From Container
    if (beforeStepChange) {
      const allow = await beforeStepChange(from, to);
      if (!allow) return;
    }

    const path = substepId ? `/step/${stepId}/${substepId}` : `/step/${stepId}`;
    navigate(path);

    // onStepChange From Container
    if (onStepChange) {
      onStepChange(stepId);
    }
  };

  // Navigation handlers
  const handleBack = () => {
    // If current step has substeps and we're on a substep
    if (currentStep.substeps && currentStep.substeps.length > 0 && currentSubstepId) {
      const substepIdx = currentStep.substeps.findIndex((s) => s.id === currentSubstepId);
      if (substepIdx > 0) {
        // Find previous available substep
        for (let i = substepIdx - 1; i >= 0; i--) {
          const prevSubstep = currentStep.substeps[i];
          if (prevSubstep.available !== false) {
            goToStep(currentStep.id, prevSubstep.id);
            return;
          }
        }
        // No previous available substep found, go to previous available main step
        if (previousAvailableStep) {
          if (previousAvailableStep.substeps && previousAvailableStep.substeps.length > 0) {
            // Find last available substep of previous step
            for (let i = previousAvailableStep.substeps.length - 1; i >= 0; i--) {
              const lastSubstep = previousAvailableStep.substeps[i];
              if (lastSubstep.available !== false) {
                goToStep(previousAvailableStep.id, lastSubstep.id);
                return;
              }
            }
          }
          goToStep(previousAvailableStep.id);
        }
        return;
      } else {
        // First substep, go to previous available main step (and its last substep if any)
        if (previousAvailableStep) {
          if (previousAvailableStep.substeps && previousAvailableStep.substeps.length > 0) {
            // Find last available substep of previous step
            for (let i = previousAvailableStep.substeps.length - 1; i >= 0; i--) {
              const lastSubstep = previousAvailableStep.substeps[i];
              if (lastSubstep.available !== false) {
                goToStep(previousAvailableStep.id, lastSubstep.id);
                return;
              }
            }
          }
          goToStep(previousAvailableStep.id);
        }
        return;
      }
    } else {
      // Not on a substep, go to previous available main step (and its last substep if any)
      if (previousAvailableStep) {
        if (previousAvailableStep.substeps && previousAvailableStep.substeps.length > 0) {
          // Find last available substep of previous step
          for (let i = previousAvailableStep.substeps.length - 1; i >= 0; i--) {
            const lastSubstep = previousAvailableStep.substeps[i];
            if (lastSubstep.available !== false) {
              goToStep(previousAvailableStep.id, lastSubstep.id);
              return;
            }
          }
        }
        goToStep(previousAvailableStep.id);
      }
    }
  };

  const handleNext = async (skipValidation: boolean = false) => {
    // beforeStepChange From Component
    setNextLoading(true);
    if (isDirty) {
      setIsDirtyAndNext(true);
      return;
    }
    
    // Skip validation if requested
    if (skipValidation) {
      setNextLoading(false);
    } else if (submitFns.current) {
      const currentSubmit = submitFns.current.at(currentStepIndex); 
      
      if (currentSubmit){
        const allow = await currentSubmit?.();
        setNextLoading(false);
        if (!allow) return;
      } else {
        setNextLoading(false);
      }
    } else {
      setNextLoading(false);
    }
           
    // If current step has substeps
    if (currentStep.substeps && currentStep.substeps.length > 0) {
      const substepIdx = currentStep.substeps.findIndex((s) => s.id === currentSubstepId);
      if (substepIdx === -1) {
        // No substep selected, go to first available substep
        for (let i = 0; i < currentStep.substeps.length; i++) {
          const firstSubstep = currentStep.substeps[i];
          if (firstSubstep.available !== false) {
            goToStep(currentStep.id, firstSubstep.id);
            return;
          }
        }
        // No available substeps, go to next available main step
        if (nextAvailableStep) {
          if (nextAvailableStep.substeps && nextAvailableStep.substeps.length > 0) {
            // Find first available substep of next step
            for (let i = 0; i < nextAvailableStep.substeps.length; i++) {
              const firstSubstep = nextAvailableStep.substeps[i];
              if (firstSubstep.available !== false) {
                goToStep(nextAvailableStep.id, firstSubstep.id);
                return;
              }
            }
          }
          goToStep(nextAvailableStep.id);
        }
        return;
      }
      if (substepIdx < currentStep.substeps.length - 1) {
        // Find next available substep
        for (let i = substepIdx + 1; i < currentStep.substeps.length; i++) {
          const nextSubstep = currentStep.substeps[i];
          if (nextSubstep.available !== false) {
            goToStep(currentStep.id, nextSubstep.id);
            return;
          }
        }
        // No next available substep found, go to next available main step
        if (nextAvailableStep) {
          if (nextAvailableStep.substeps && nextAvailableStep.substeps.length > 0) {
            // Find first available substep of next step
            for (let i = 0; i < nextAvailableStep.substeps.length; i++) {
              const firstSubstep = nextAvailableStep.substeps[i];
              if (firstSubstep.available !== false) {
                goToStep(nextAvailableStep.id, firstSubstep.id);
                return;
              }
            }
          }
          goToStep(nextAvailableStep.id);
        }
        return;
      }
      // Last substep, go to next available main step
      if (nextAvailableStep) {
        if (nextAvailableStep.substeps && nextAvailableStep.substeps.length > 0) {
          // Find first available substep of next step
          for (let i = 0; i < nextAvailableStep.substeps.length; i++) {
            const firstSubstep = nextAvailableStep.substeps[i];
            if (firstSubstep.available !== false) {
              goToStep(nextAvailableStep.id, firstSubstep.id);
              return;
            }
          }
        }
        goToStep(nextAvailableStep.id);
        return;
      }
    } else {
      // No substeps, go to next available main step
      if (nextAvailableStep) {
        if (nextAvailableStep.substeps && nextAvailableStep.substeps.length > 0) {
          // Find first available substep of next step
          for (let i = 0; i < nextAvailableStep.substeps.length; i++) {
            const firstSubstep = nextAvailableStep.substeps[i];
            if (firstSubstep.available !== false) {
              goToStep(nextAvailableStep.id, firstSubstep.id);
              return;
            }
          }
        }
        goToStep(nextAvailableStep.id);
      }
    }
  };

  const handleStepClick = (stepId: string, substepId?: string) => {
    // There is a design problem by which the MultiStepper component is triggering the
    // `handleStepClick` function not strictly only when the step is clicked, but when
    // the step is changed. This is a workaround to ensure that the substep is not lost.
    // There needs to be a deeper investigation into the right approach here, but this fixes
    // a lot of problems for now.

    // If the stepId is set and the substepId is not provided, and we have a current step equal to the desired step, and the current substep is set,
    if (stepId === currentStep.id && !substepId) {
      // use that current substep
      substepId = currentSubstepId;
    }

    // If there are substeps for the stepId and there is no substepId, use the first available substep
    if (!substepId && steps.find((s) => s.id === stepId)?.substeps) {
      const step = steps.find((s) => s.id === stepId);
      if (step?.substeps && step.substeps.length > 0) {
        // Find first available substep
        for (let i = 0; i < step.substeps.length; i++) {
          const firstSubstep = step.substeps[i];
          if (firstSubstep.available !== false) {
            substepId = firstSubstep.id;
            break;
          }
        }
      }
    }

    goToStep(stepId, substepId);
  };

  const registerSubmit = (submitFn: () => Promise<boolean> | boolean) => {
    submitFns.current[currentStepIndex] = submitFn;
  };

  // Track URL changes and update state
  useEffect(() => {
    const { urlStepId: newUrlStepId, urlSubstepId: newUrlSubstepId } = parseUrlPath();
    if (currentStep.id !== newUrlStepId || currentSubstepId !== newUrlSubstepId) {
      goToStep(newUrlStepId, newUrlSubstepId);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return {
    currentStep,
    currentStepIndex,
    currentSubstepId,
    isFirstStep,
    isLastStep,
    handleBack,
    handleNext,
    handleStepClick,
    goToStep,
    registerSubmit,
    nextLoading,
    isDirtyAndNext
  };
}; 